import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  CheckSquare,
  Clock,
  Inbox,
  Circle,
  AlertTriangle,
  Hourglass,
  MessageSquare,
  User,
  X,
  Bell,
  AlarmClock,
  ArrowRight,
} from 'lucide-react';
import { apiGet, apiPatch, apiPost } from '../lib/api';
import { formatRelativeTime, useNow } from '../lib/relativeTime';

interface DashboardStats {
  openTasks: number;
  dueThisWeek: number;
  meetingsToday: number;
  inboxItems: number;
}

interface DashboardTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  person_id?: string;
  person_name?: string;
}

interface DashboardMeeting {
  id: string;
  title: string | null;
  meeting_date: string;
  series_name: string;
  series_id: string;
}

interface DashboardCapture {
  id: string;
  text: string;
  status: string;
  created_at: string;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  todo: <Circle size={12} className="text-text-muted" />,
  next: <ArrowRight size={12} className="text-sky-400" />,
  in_progress: <Clock size={12} className="text-accent" />,
  blocked: <AlertTriangle size={12} className="text-danger" />,
  done: <CheckSquare size={12} className="text-success" />,
  cancelled: <X size={12} className="text-text-muted" />,
};

const PRIORITY_DOT_COLORS: Record<string, string> = {
  urgent: 'bg-danger',
  high: 'bg-warning',
  medium: 'bg-text-secondary',
  low: 'bg-text-muted',
};

export function DashboardPage() {
  useNow(); // tick every 30s so relative timestamps stay fresh
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [overdueTasks, setOverdueTasks] = useState<DashboardTask[]>([]);
  const [dueThisWeek, setDueThisWeek] = useState<DashboardTask[]>([]);
  const [meetingsToday, setMeetingsToday] = useState<DashboardMeeting[]>([]);
  const [recentCaptures, setRecentCaptures] = useState<DashboardCapture[]>([]);
  const [waitingOn, setWaitingOn] = useState<DashboardTask[]>([]);
  const [followUps, setFollowUps] = useState<DashboardTask[]>([]);
  const [upcomingReminders, setUpcomingReminders] = useState<{ id: string; title: string; remind_at: string; status: string }[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, o, d, m, c, w, f] = await Promise.all([
        apiGet<DashboardStats>('/dashboard/stats'),
        apiGet<DashboardTask[]>('/dashboard/overdue'),
        apiGet<DashboardTask[]>('/dashboard/due-this-week'),
        apiGet<DashboardMeeting[]>('/dashboard/meetings-today'),
        apiGet<DashboardCapture[]>('/dashboard/recent-captures'),
        apiGet<DashboardTask[]>('/dashboard/waiting-on'),
        apiGet<DashboardTask[]>('/dashboard/follow-ups'),
      ]);
      setStats(s);
      setOverdueTasks(o);
      setDueThisWeek(d);
      setMeetingsToday(m);
      setRecentCaptures(c);
      setWaitingOn(w);
      setFollowUps(f);
      // Load today's reminders (non-blocking)
      apiGet<{ id: string; title: string; remind_at: string; status: string }[]>('/dashboard/todays-reminders')
        .then(r => setUpcomingReminders(r.slice(0, 5)))
        .catch(() => {});
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStatusChange = useCallback(async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'done' ? 'todo' : 'done';
    try {
      await apiPatch(`/tasks/${taskId}`, { status: newStatus });
      load();
    } catch (err) {
      console.error('Failed to update task status:', err);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  // Refresh when a quick capture is added from Header
  useEffect(() => {
    function onCapture() { load(); }
    window.addEventListener('quick-capture-added', onCapture);
    return () => window.removeEventListener('quick-capture-added', onCapture);
  }, [load]);

  if (loading) {
    return (
      <div className="h-full overflow-auto p-3 sm:p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  // Group waiting-on tasks by person
  const waitingByPerson = new Map<string, { name: string; tasks: DashboardTask[] }>();
  for (const t of waitingOn) {
    const key = t.person_id || 'unknown';
    if (!waitingByPerson.has(key)) {
      waitingByPerson.set(key, { name: t.person_name || 'Unknown', tasks: [] });
    }
    waitingByPerson.get(key)!.tasks.push(t);
  }

  // Group follow-ups by person
  const followUpsByPerson = new Map<string, { name: string; tasks: DashboardTask[] }>();
  for (const t of followUps) {
    const key = t.person_id || 'unknown';
    if (!followUpsByPerson.has(key)) {
      followUpsByPerson.set(key, { name: t.person_name || 'Unknown', tasks: [] });
    }
    followUpsByPerson.get(key)!.tasks.push(t);
  }

  return (
    <div className="h-full overflow-auto p-3 sm:p-6">
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Dashboard</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={CheckSquare} label="Open Tasks" value={stats?.openTasks ?? 0} to="/tasks" />
        <StatCard icon={Clock} label="Due This Week" value={stats?.dueThisWeek ?? 0} to="/tasks" />
        <StatCard icon={Calendar} label="Meetings Today" value={stats?.meetingsToday ?? 0} to="/meetings" />
        <StatCard icon={Inbox} label="Inbox Items" value={stats?.inboxItems ?? 0} to="/inbox" />
      </div>

      {/* Main grid — masonry layout via CSS columns */}
      <div className="columns-1 lg:columns-2 gap-6 space-y-6">
        {/* Today's Meetings */}
        <Section title="Today's Meetings" icon={<Calendar size={14} />}>
          {meetingsToday.length === 0 ? (
            <EmptyState message="No meetings scheduled for today." />
          ) : (
            <div className="space-y-1">
              {meetingsToday.map(m => (
                <Link
                  key={m.id}
                  to={`/meetings/${m.series_id}/${m.id}`}
                  className="flex items-center gap-2 px-2 py-2 rounded hover:bg-bg-hover transition-colors group"
                >
                  <Calendar size={14} className="text-warning flex-shrink-0" />
                  <span className="text-sm text-text-primary group-hover:text-accent transition-colors truncate flex-1">
                    {m.title || m.series_name}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* Overdue Tasks */}
        <Section title="Overdue Tasks" icon={<AlertTriangle size={14} className="text-danger" />}>
          {overdueTasks.length === 0 ? (
            <EmptyState message="No overdue tasks. You're all caught up." />
          ) : (
            <div className="space-y-0.5">
              {overdueTasks.map(t => <TaskRow key={t.id} task={t} showDueDate onStatusChange={handleStatusChange} />)}
            </div>
          )}
        </Section>

        {/* Tasks Due This Week */}
        <Section title="Due This Week" icon={<Clock size={14} />}>
          {dueThisWeek.length === 0 ? (
            <EmptyState message="No tasks due this week." />
          ) : (
            <div className="space-y-0.5">
              {dueThisWeek.map(t => <TaskRow key={t.id} task={t} showDueDate onStatusChange={handleStatusChange} />)}
            </div>
          )}
        </Section>

        {/* Recent Quick Captures */}
        <Section title="Inbox" icon={<Inbox size={14} />}>
          {recentCaptures.length === 0 ? (
            <EmptyState message="Nothing in your inbox." />
          ) : (
            <div className="space-y-1">
              {recentCaptures.map(c => (
                <Link key={c.id} to="/inbox" className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-bg-hover transition-colors group">
                  <MessageSquare size={12} className="text-text-muted mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary group-hover:text-accent transition-colors truncate">{c.text}</p>
                    <p className="text-[10px] text-text-muted">{formatRelativeTime(c.created_at)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* Today's Reminders */}
        <Section title="Today's Reminders" icon={<Bell size={14} />}>
          {upcomingReminders.length === 0 ? (
            <EmptyState message="No reminders for today." />
          ) : (
            <div className="space-y-1">
              {upcomingReminders.map(r => {
                const timeStr = new Date(r.remind_at).toLocaleString(undefined, {
                  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                });
                return (
                  <Link key={r.id} to="/reminders" className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-bg-hover transition-colors group">
                    {r.status === 'snoozed'
                      ? <AlarmClock size={12} className="text-warning flex-shrink-0" />
                      : <Clock size={12} className="text-text-muted flex-shrink-0" />
                    }
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-text-primary group-hover:text-accent transition-colors truncate">{r.title}</p>
                      <p className="text-[10px] text-text-muted">{timeStr}</p>
                    </div>
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        await apiPost(`/reminders/${r.id}/snooze`, { minutes: 15 });
                        load();
                      }}
                      className="p-0.5 text-text-muted hover:text-warning opacity-0 group-hover:opacity-100 transition-all"
                      title="Snooze 15m"
                    >
                      <AlarmClock size={12} />
                    </button>
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        await apiPost(`/reminders/${r.id}/dismiss`, {});
                        load();
                      }}
                      className="p-0.5 text-text-muted hover:text-text-primary opacity-0 group-hover:opacity-100 transition-all"
                      title="Dismiss"
                    >
                      <X size={12} />
                    </button>
                  </Link>
                );
              })}
            </div>
          )}
        </Section>

        {/* Waiting On */}
        <Section title="Waiting On" icon={<Hourglass size={14} className="text-warning" />}>
          {waitingByPerson.size === 0 ? (
            <EmptyState message="No tasks waiting on anyone." />
          ) : (
            <div className="space-y-3">
              {[...waitingByPerson.entries()].map(([personId, group]) => (
                <div key={personId}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <User size={12} className="text-text-muted" />
                    <span className="text-xs font-semibold text-text-secondary">{group.name}</span>
                  </div>
                  <div className="space-y-0.5 pl-4">
                    {group.tasks.map(t => <TaskRow key={t.id} task={t} onStatusChange={handleStatusChange} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Follow Ups */}
        <Section title="Follow Ups" icon={<MessageSquare size={14} className="text-accent" />}>
          {followUpsByPerson.size === 0 ? (
            <EmptyState message="No follow-up tasks." />
          ) : (
            <div className="space-y-3">
              {[...followUpsByPerson.entries()].map(([personId, group]) => (
                <div key={personId}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <User size={12} className="text-text-muted" />
                    <span className="text-xs font-semibold text-text-secondary">{group.name}</span>
                  </div>
                  <div className="space-y-0.5 pl-4">
                    {group.tasks.map(t => <TaskRow key={t.id} task={t} onStatusChange={handleStatusChange} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
    </div>
  );
}

function TaskRow({ task, showDueDate, onStatusChange }: { task: DashboardTask; showDueDate?: boolean; onStatusChange?: (id: string, status: string) => void }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-bg-hover transition-colors group">
      <button
        type="button"
        className="flex-shrink-0 cursor-pointer hover:opacity-70 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onStatusChange?.(task.id, task.status);
        }}
        title={`Mark as ${task.status === 'done' ? 'todo' : 'done'}`}
      >
        {STATUS_ICONS[task.status] ?? <Circle size={12} />}
      </button>
      <Link
        to={`/tasks/${task.id}`}
        state={{ from: '/', fromLabel: 'Dashboard' }}
        className="text-sm text-text-primary group-hover:text-accent transition-colors truncate flex-1"
      >
        {task.title}
      </Link>
      {showDueDate && task.due_date && (
        <span className="text-[10px] text-text-muted flex-shrink-0">
          {formatShortDate(task.due_date)}
        </span>
      )}
      <span
        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
          PRIORITY_DOT_COLORS[task.priority] ?? 'bg-text-muted'
        }`}
        title={task.priority}
      />
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  to,
}: {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  value: number;
  to?: string;
}) {
  const content = (
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-md bg-accent/10 text-accent">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-bold text-text-primary">{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="bg-bg-card border border-border rounded-lg p-4 hover:border-accent/50 transition-colors">
        {content}
      </Link>
    );
  }

  return <div className="bg-bg-card border border-border rounded-lg p-4">{content}</div>;
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-bg-card border border-border rounded-lg break-inside-avoid">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold text-text-secondary">{title}</h2>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="text-sm text-text-muted text-center py-4">{message}</p>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00'));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
